import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Add, Save, Delete, Edit, CloudUpload } from '@mui/icons-material';
import { financialDocumentService, extractApiDataList } from '../services/api';
import { getAssetUrl } from '../config';

const emptyForm = {
  title: '',
  display_order: '0',
  is_active: '1',
};

const ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,application/pdf';

const FinancialDocumentsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [newRow, setNewRow] = useState(emptyForm);
  const [newFile, setNewFile] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(emptyForm);
  const [editFile, setEditFile] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [loadError, setLoadError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const res = await financialDocumentService.getAll({ include_inactive: 1 });
      setRows(extractApiDataList(res));
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Failed to load documents';
      setLoadError(msg);
      setRows([]);
      setToast({ open: true, message: msg, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const buildFormData = (row, file) => {
    const fd = new FormData();
    fd.append('title', row.title.trim());
    fd.append('display_order', String(row.display_order === '' ? 0 : row.display_order));
    fd.append('is_active', row.is_active === '1' ? '1' : '0');
    if (file) fd.append('file', file);
    return fd;
  };

  const handleAdd = async () => {
    if (!newRow.title.trim()) {
      setToast({ open: true, message: 'Document name is required', type: 'error' });
      return;
    }
    if (!newFile) {
      setToast({ open: true, message: 'Please upload a document file', type: 'error' });
      return;
    }
    try {
      await financialDocumentService.create(buildFormData(newRow, newFile));
      setNewRow(emptyForm);
      setNewFile(null);
      setToast({ open: true, message: 'Document added', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Add failed', type: 'error' });
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditFile(null);
    setEditRow({
      title: row.title || '',
      display_order: String(row.display_order ?? 0),
      is_active: row.is_active === 0 || row.is_active === false || row.is_active === '0' ? '0' : '1',
    });
  };

  const handleSaveEdit = async () => {
    if (!editId || !editRow.title.trim()) return;
    try {
      await financialDocumentService.update(editId, buildFormData(editRow, editFile));
      setEditId(null);
      setEditFile(null);
      setToast({ open: true, message: 'Document updated', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Update failed', type: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await financialDocumentService.delete(id);
      setToast({ open: true, message: 'Document deleted', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Delete failed', type: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/dashboard')} sx={{ mb: 2, color: '#2c5f2d' }}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Financial & Legal Documents
        </Typography>
        <Typography variant="body2" sx={{ color: '#667085', mb: 3 }}>
          Manage document names and files shown on the public Financial & Legal Documents page.
        </Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {loadError}
          </Alert>
        )}

        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Add document
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Document name *"
                placeholder="80G Certificate"
                value={newRow.title}
                onChange={(e) => setNewRow({ ...newRow, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />} fullWidth>
                {newFile ? newFile.name : 'Upload file *'}
                <input
                  type="file"
                  hidden
                  accept={ACCEPT}
                  onChange={(e) => setNewFile(e.target.files?.[0] || null)}
                />
              </Button>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Order"
                value={newRow.display_order}
                onChange={(e) => setNewRow({ ...newRow, display_order: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField
                select
                fullWidth
                size="small"
                label="Active"
                value={newRow.is_active}
                onChange={(e) => setNewRow({ ...newRow, is_active: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="1">Yes</option>
                <option value="0">No</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button variant="contained" startIcon={<Add />} onClick={handleAdd} sx={{ backgroundColor: '#2c5f2d' }}>
                Add
              </Button>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ border: '1px solid #e8ecf1' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafb' }}>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#667085' }}>
                      No documents yet. Add one above, then run sql/006_financial_documents.sql if the table is missing.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ minWidth: 220 }}>
                        {editId === row.id ? (
                          <TextField
                            size="small"
                            fullWidth
                            value={editRow.title}
                            onChange={(e) => setEditRow({ ...editRow, title: e.target.value })}
                          />
                        ) : (
                          row.title
                        )}
                      </TableCell>
                      <TableCell>
                        {editId === row.id ? (
                          <Button variant="outlined" component="label" size="small" startIcon={<CloudUpload />}>
                            {editFile ? editFile.name : 'Replace file'}
                            <input
                              type="file"
                              hidden
                              accept={ACCEPT}
                              onChange={(e) => setEditFile(e.target.files?.[0] || null)}
                            />
                          </Button>
                        ) : row.file_url ? (
                          <Link href={getAssetUrl(row.file_url)} target="_blank" rel="noopener noreferrer">
                            {row.original_name || 'View file'}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 100 }}>
                        {editId === row.id ? (
                          <TextField
                            size="small"
                            type="number"
                            value={editRow.display_order}
                            onChange={(e) => setEditRow({ ...editRow, display_order: e.target.value })}
                          />
                        ) : (
                          row.display_order
                        )}
                      </TableCell>
                      <TableCell>
                        {editId === row.id ? (
                          <TextField
                            select
                            size="small"
                            value={editRow.is_active}
                            onChange={(e) => setEditRow({ ...editRow, is_active: e.target.value })}
                            SelectProps={{ native: true }}
                          >
                            <option value="1">Yes</option>
                            <option value="0">No</option>
                          </TextField>
                        ) : (
                          <Chip
                            size="small"
                            label={row.is_active === 0 || row.is_active === '0' || row.is_active === false ? 'Inactive' : 'Active'}
                            color={row.is_active === 0 || row.is_active === '0' || row.is_active === false ? 'default' : 'success'}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {editId === row.id ? (
                          <>
                            <IconButton color="success" onClick={handleSaveEdit}><Save /></IconButton>
                            <Button size="small" onClick={() => { setEditId(null); setEditFile(null); }}>Cancel</Button>
                          </>
                        ) : (
                          <>
                            <IconButton color="primary" onClick={() => startEdit(row)}><Edit /></IconButton>
                            <IconButton color="error" onClick={() => handleDelete(row.id)}><Delete /></IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast({ ...toast, open: false })}>
          <Alert severity={toast.type} onClose={() => setToast({ ...toast, open: false })}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default FinancialDocumentsPage;
